import { useMemo } from 'react';

import { Nil } from '@core';
import { SwingThreadWithTitle as SwingThreadModel } from '@data';
import { useSwingThreadSubscription } from '@hooks';

import { ScrubberMark } from './components/ScrubberMark';
import { MARKER_RADIUS } from './consts';

type Marker = Pick<SwingThreadModel, 'fromFrame' | 'id' | 'createdBy' | 'swingUuid' | 'linkNodeId'>;

export type MarkerWithPosition = Marker & {
    pPosition: number;
};

type PositionMarker = {
    position: number;
    ratio: number;
};

export type ScrubberMarksProps = {
    totalFrames?: number | Nil;
    swingUuid: string;
    markers: Marker[];
    pPositions: PositionMarker[];
    width?: number | Nil;
    activeUserId: string;
    openThreads: Record<number, boolean>;
    setOpenThreads: (threads: Record<number, boolean>) => void;
};

const getPositionForRatio = (ratio: number, pPositions: PositionMarker[]): number | null => {
    if (!pPositions.length) return null;
    // Find the first position ratio that is bigger than the parameter ratio
    const idx = pPositions.findIndex((p) => ratio < p.ratio);
    if (idx === 0) return pPositions[0].position;
    if (idx === -1) return pPositions[pPositions.length - 1].position;
    return pPositions[idx - 1].position;
};

const createMarkersClusters = ({
    markers,
    totalFrames,
    width,
    pPositions,
}: Omit<ScrubberMarksProps, 'swingUuid' | 'activeUserId' | 'openThreads' | 'setOpenThreads'>): Record<
    number,
    MarkerWithPosition[]
> => {
    if (!width || !totalFrames) return {};

    return markers
        .sort((a, b) => a.fromFrame - b.fromFrame)
        .reduce<Record<number, MarkerWithPosition[]>>((clusteredMarkers, curr) => {
            const clusterKeys = Object.keys(clusteredMarkers);
            const lastKey = clusterKeys.at(-1);
            const pPosition = getPositionForRatio((curr.fromFrame / totalFrames) * width, pPositions);
            if (pPosition === null) {
                return clusteredMarkers;
            }
            const markerWithPPosition = { ...curr, pPosition };
            if (!lastKey) {
                clusteredMarkers[curr.fromFrame] = [markerWithPPosition];
                return clusteredMarkers;
            }

            const lastFrameFrom = +lastKey;
            const lastPx = (lastFrameFrom / totalFrames) * width;
            const currPx = (curr.fromFrame / totalFrames) * width;

            if (currPx - lastPx > MARKER_RADIUS * 2) {
                clusteredMarkers[curr.fromFrame] = [markerWithPPosition];
            } else {
                clusteredMarkers[lastFrameFrom] = [...(clusteredMarkers[lastFrameFrom] ?? []), markerWithPPosition];
            }

            return clusteredMarkers;
        }, {});
};

export const ScrubberMarks = ({
    totalFrames,
    markers,
    swingUuid,
    pPositions,
    width,
    activeUserId,
    openThreads,
    setOpenThreads,
}: ScrubberMarksProps) => {
    useSwingThreadSubscription(swingUuid);

    const clusteredMarkers = useMemo(
        () => createMarkersClusters({ markers, width, totalFrames, pPositions }),
        [markers, pPositions, totalFrames, width],
    );
    return Object.entries(clusteredMarkers).map(([fromFrame, markers]) => (
        <ScrubberMark
            fromFrame={+fromFrame}
            swingUuid={swingUuid}
            key={fromFrame}
            left={`${(+fromFrame / (totalFrames ?? 0)) * 100}%`}
            markers={markers}
            totalFrames={totalFrames}
            activeUserId={activeUserId}
            openThreads={openThreads}
            setOpenThreads={setOpenThreads}
        />
    ));
};
